import { encodeFunctionData, isAddress, type Hex } from "viem";
import { botnetRegistryV2Abi } from "@botnet/shared/abi-v2";
import type { DeploymentReference, PublicV2Purchase } from "./public-v2";

export type RegistryCreditWithdrawalIntent = Readonly<{
  schemaVersion: 1;
  chainId: 8453;
  kind: "refundAfterDeadline" | "withdrawCredit";
  agentId: string;
  registryAddress: `0x${string}`;
  beneficiary: `0x${string}`;
  destination: `0x${string}`;
  amount: string;
  startingCredit: string;
  preparedBlockNumber: string;
  preparedBlockHash: `0x${string}`;
  transactionHash: Hex | null;
}>;

const HASH = /^0x[0-9a-f]{64}$/;
const DECIMAL = /^(0|[1-9][0-9]*)$/;
const ZERO_ADDRESS = `0x${"0".repeat(40)}`;
const MAX_UINT256 = (1n << 256n) - 1n;
const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);

export function isRefundedRegistryPurchase(purchase: PublicV2Purchase): boolean {
  return purchase.phase === "refunded" && purchase.refundCredited === true && purchase.settlementCredited === false;
}

export function isDeadlineRefundCandidate(purchase: PublicV2Purchase, chainPurchase: { exists: boolean; phase: number; payer: string }, refundAvailable: boolean): boolean {
  return purchase.phase === "queued" && purchase.refundCredited === false && purchase.settlementCredited === false &&
    chainPurchase.exists === true && chainPurchase.phase === 2 && chainPurchase.payer.toLowerCase() === purchase.payer.toLowerCase() && refundAvailable === true;
}

export function registryCreditWithdrawalKey(deployment: DeploymentReference, beneficiary: string): string | null {
  const account = beneficiary.toLowerCase();
  if (!isAddress(account) || account === ZERO_ADDRESS || deployment.chainId !== 8453 || !isAddress(deployment.registryAddress)) return null;
  return `botnet.registry-credit-withdrawal.v1:8453:${deployment.registryAddress.toLowerCase()}:${account}`;
}

export function makeRegistryCreditWithdrawalIntent(input: {
  deployment: DeploymentReference;
  kind: RegistryCreditWithdrawalIntent["kind"];
  agentId: string;
  beneficiary: string;
  amount: bigint;
  startingCredit: bigint;
  preparedBlockNumber: bigint;
  preparedBlockHash: string;
}): RegistryCreditWithdrawalIntent {
  const beneficiary = input.beneficiary.toLowerCase();
  if (input.deployment.chainId !== 8453 || !isAddress(input.deployment.registryAddress) || !isAddress(beneficiary) || beneficiary === ZERO_ADDRESS ||
      input.kind !== "refundAfterDeadline" && input.kind !== "withdrawCredit" || input.agentId.length > 78 || !DECIMAL.test(input.agentId) || BigInt(input.agentId) <= 0n || BigInt(input.agentId) > MAX_UINT256 ||
      input.amount <= 0n || input.amount > MAX_UINT256 || input.startingCredit < 0n || input.startingCredit > MAX_UINT256 ||
      input.preparedBlockNumber < 0n || input.preparedBlockNumber > MAX_SAFE || !HASH.test(input.preparedBlockHash)) throw new TypeError("invalid withdrawal intent");
  return Object.freeze({ schemaVersion: 1, chainId: 8453, kind: input.kind, agentId: input.agentId, registryAddress: input.deployment.registryAddress.toLowerCase() as `0x${string}`,
    beneficiary: beneficiary as `0x${string}`, destination: beneficiary as `0x${string}`, amount: input.amount.toString(),
    startingCredit: input.startingCredit.toString(),
    preparedBlockNumber: input.preparedBlockNumber.toString(), preparedBlockHash: input.preparedBlockHash.toLowerCase() as `0x${string}`, transactionHash: null });
}

export function withRegistryCreditWithdrawalHash(intent: RegistryCreditWithdrawalIntent, value: unknown): RegistryCreditWithdrawalIntent {
  if (typeof value !== "string" || !HASH.test(value)) throw new TypeError("invalid transaction hash");
  return Object.freeze({ ...intent, transactionHash: value.toLowerCase() as Hex });
}

export function parseRegistryCreditWithdrawalIntent(value: unknown, deployment: DeploymentReference, beneficiary: string): RegistryCreditWithdrawalIntent | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const account = beneficiary.toLowerCase();
  const keys = ["schemaVersion", "chainId", "kind", "agentId", "registryAddress", "beneficiary", "destination", "amount", "startingCredit", "preparedBlockNumber", "preparedBlockHash", "transactionHash"];
  if (Object.keys(row).length !== keys.length || Object.keys(row).some((key) => !keys.includes(key)) ||
      row.schemaVersion !== 1 || row.chainId !== 8453 || deployment.chainId !== 8453 ||
      row.kind !== "refundAfterDeadline" && row.kind !== "withdrawCredit" || typeof row.agentId !== "string" || row.agentId.length > 78 || !DECIMAL.test(row.agentId) || BigInt(row.agentId) <= 0n || BigInt(row.agentId) > MAX_UINT256 ||
      typeof row.registryAddress !== "string" || row.registryAddress !== deployment.registryAddress.toLowerCase() ||
      row.beneficiary !== account || row.destination !== account || !isAddress(account) || account === ZERO_ADDRESS ||
      typeof row.amount !== "string" || row.amount.length > 78 || !DECIMAL.test(row.amount) || BigInt(row.amount) <= 0n || BigInt(row.amount) > MAX_UINT256 ||
      typeof row.startingCredit !== "string" || row.startingCredit.length > 78 || !DECIMAL.test(row.startingCredit) || BigInt(row.startingCredit) > MAX_UINT256 ||
      typeof row.preparedBlockNumber !== "string" || row.preparedBlockNumber.length > 16 || !DECIMAL.test(row.preparedBlockNumber) || BigInt(row.preparedBlockNumber) > MAX_SAFE ||
      typeof row.preparedBlockHash !== "string" || !HASH.test(row.preparedBlockHash) || row.preparedBlockHash !== row.preparedBlockHash.toLowerCase() ||
      (row.transactionHash !== null && (typeof row.transactionHash !== "string" || !HASH.test(row.transactionHash))) ) return null;
  return Object.freeze(row as unknown as RegistryCreditWithdrawalIntent);
}

export function registryCreditWithdrawalData(beneficiary: `0x${string}`): Hex {
  return encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "withdrawCredit", args: [beneficiary, beneficiary] });
}

export function registryCreditActionData(intent: Pick<RegistryCreditWithdrawalIntent, "kind" | "agentId" | "beneficiary">): Hex {
  return intent.kind === "refundAfterDeadline"
    ? encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "refundAfterDeadline", args: [BigInt(intent.agentId)] })
    : registryCreditWithdrawalData(intent.beneficiary);
}

export function exactRegistryCreditWithdrawalTransaction(value: unknown, intent: RegistryCreditWithdrawalIntent): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const tx = value as Record<string, unknown>;
  return typeof tx.from === "string" && tx.from.toLowerCase() === intent.beneficiary &&
    typeof tx.to === "string" && tx.to.toLowerCase() === intent.registryAddress &&
    typeof tx.input === "string" && tx.input.toLowerCase() === registryCreditActionData(intent).toLowerCase() &&
    typeof tx.value === "bigint" && tx.value === 0n;
}
